import React, { useEffect } from 'react';
import { createStyles, makeStyles } from '@material-ui/core';
import {
  Grid, Button, Card, Box, CardContent, useMediaQuery, useTheme, DialogTitle, DialogContent, DialogContentText, DialogActions, Dialog, Theme
} from '@mui/material';
import {
  Formik, Form, FormikProps, Field
} from 'formik';
import * as Yup from 'yup';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { IconName } from '@fortawesome/fontawesome-svg-core';

import { useAuth } from '../../../../contexts/auth';
import WrapperSimple from '../../../../layout-components/ExampleWrapperSimple';
import Dictionary from '../../../../models/interfaces/dictionary';
import NoData from '../../../../components/noData';
import ExportPDF from '../../../../components/exportPDF';
import ExportCSV from '../../../../components/exportExcel';
import { getFirstDate, getLastDate } from '../../../../utils/dateHelper';
import Loading from '../../../../components/loading';
import PrepagPrint from './prepagoPrint';
import customerService from '../../../../services/customerService';
import { toBrazilDate, toBrazilDateTime, toBrazilTime } from '../../../../components/mask';
import balanceStatus, { BalanceStatusText } from '../../../../models/enums/balanceStatus';
import { PaymentBalanceType, PaymentBalanceTypeList } from '../../../../models/enums/balanceType';
import balanceService from '../../../../services/balanceService';
import userService from '../../../../services/userService';
import authService from '../../../../services/authService';
import useDebounce from '../../../../hooks/useDebounce';
import AutocompleteCustomField from '../../../../components/autocompleteCustomField';

interface Formulario {
  client: Dictionary | null;
  paymentType: Dictionary | null;
  status: Dictionary | null;
  startDate: Date | null;
  endDate: Date | null;
}

export interface Row {
  id: string;
  createdAt: Date | null;
  approvedBy: string;
  approvedDate: Date | null;
  comments: string | null;
  description: string;
  payment: string;
  previousBalance: number;
  receiptUrl: string | null;
  status: string;
  icon: IconName;
  isPositive: boolean;
  enableButton: boolean;
  value: number;
  creditValue: number;
  pixPayerCpf?: string | null;
  pixPayerName?: string | null;
}

interface OwnProps {
  subsidiaryId: string;
  canApprove?: boolean;
}

const useStyles = makeStyles((theme: Theme) => createStyles({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  },
  selectEmpty: {
    marginTop: theme.spacing(2)
  },
  selectRow: {
    cursor: 'pointer'
  }
}));

const getPaymentDescription = (payment: string | number | null | undefined): string => {
  if (payment === null || payment === undefined || payment === '') return '-';
  const paymentKey = Number(payment);
  return PaymentBalanceTypeList.find((item) => item.key === paymentKey)?.value
    ?? String(payment);
};

const paymentTypeOptions: Dictionary[] = [
  { key: null, value: 'Todos' },
  ...PaymentBalanceTypeList
];

const statusOptions: Dictionary[] = [
  { key: null, value: 'Todos' },
  { key: balanceStatus.Waiting, value: BalanceStatusText[balanceStatus.Waiting] },
  { key: balanceStatus.Approved, value: BalanceStatusText[balanceStatus.Approved] },
  { key: balanceStatus.Canceled, value: BalanceStatusText[balanceStatus.Canceled] }
];

const looksLikeUserId = (value: string) => !value.includes(' ') && value.length >= 20;

const getReceiptUrl = (balance: { receiptUrl?: string | null } & Record<string, any>): string | null => {
  const candidates = [balance.receiptUrl, balance.receiptURL, balance.receipt];
  const url = candidates.find((item) => typeof item === 'string' && item.trim().length > 0);
  return url ? String(url).trim() : null;
};

const getReceiptPreviewKind = (url: string): 'image' | 'pdf' | 'other' => {
  const cleanUrl = url.split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp)$/.test(cleanUrl)) return 'image';
  if (/\.pdf$/.test(cleanUrl)) return 'pdf';
  return 'other';
};

const resolveApproverNames = async (approverIds: Array<string | null | undefined>): Promise<Record<string, string>> => {
  const uniqueIds = Array.from(new Set(
    approverIds.filter((id): id is string => typeof id === 'string' && looksLikeUserId(id))
  ));

  const entries = await Promise.all(uniqueIds.map(async (id) => {
    try {
      const approver = await userService.getByIdAsync(id);
      return [id, approver.name || approver.displayName || id] as const;
    } catch {
      return [id, id] as const;
    }
  }));

  return Object.fromEntries(entries);
};

const page = (own: OwnProps) => {
  const auth = useAuth();
  const { user } = auth;
  let { subsidiaryId } = auth;
  const { enqueueSnackbar } = useSnackbar();
  const [rowsTable, setRows] = React.useState<Row[]>([]);
  const [selectedRow, setSelectedRow] = React.useState<Row | null>(null);
  const [clients, setClients] = React.useState<Dictionary[]>([]);
  const [valuesForm, setValuesForm] = React.useState<Formulario>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isApproving, setIsApproving] = React.useState(false);
  const [inputCustomerValue, setInputCustomerValue] = React.useState('');
  if (own.subsidiaryId) subsidiaryId = own.subsidiaryId;
  const canApprove = own.canApprove !== false;
  const classes = useStyles();

  const [openApprove, setOpenApprove] = React.useState(false);
  const [openDetail, setOpenDetail] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClickOpen = (item: Row) => {
    if (!canApprove) {
      setSelectedRow(item);
      setOpenDetail(true);
      return;
    }

    if (!item.enableButton) return;

    setSelectedRow(item);
    setOpenApprove(true);
  };

  const handleCloseApprove = () => {
    if (isApproving) return;
    setSelectedRow(null);
    setOpenApprove(false);
  };

  const handleCloseDetail = () => {
    setSelectedRow(null);
    setOpenDetail(false);
  };

  const debouncedFetchCustomer = useDebounce((str: string) => {
    if (str.length === 0) return;
    customerService.getListForAutocomplete(subsidiaryId, str).then((res) => {
      if (!res?.hits?.length) return;
      const list = res?.hits.map((x: any) => ({ key: x.objectID, value: x.name ?? x.businessName }));
      list.unshift({ key: null, value: 'Todos' });
      setClients(list);
    });
  });

  const ensureApproverIsActive = async () => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado. Faça login novamente para aprovar o crédito.');
    }

    const currentUser = await userService.getByIdAsync(user.uid, user.email);
    if (currentUser.disabledAt || currentUser.disabled) {
      await authService.signOut().catch(() => undefined);
      throw new Error('Seu usuário está inativo na plataforma e não pode aprovar créditos.');
    }
  };

  const handleConfirm = async () => {
    if (!selectedRow || isApproving) return;

    setIsApproving(true);
    try {
      await ensureApproverIsActive();
      await balanceService.approveAsync(selectedRow.id);

      const customRows = [...rowsTable];
      const index = customRows.findIndex(((x) => x.id === selectedRow.id));
      if (index >= 0) {
        customRows[index] = {
          ...customRows[index],
          icon: 'check-circle',
          status: BalanceStatusText[balanceStatus.Approved],
          enableButton: false,
          approvedBy: user?.name || customRows[index].approvedBy,
          approvedDate: new Date()
        };
        setRows(customRows);
      }

      enqueueSnackbar('Crédito aprovado com sucesso!', { variant: 'success' });
      setSelectedRow(null);
      setOpenApprove(false);
    } catch (e: any) {
      enqueueSnackbar(e.message ?? 'Não foi possível aprovar o crédito.', { variant: 'error' });
      setSelectedRow(null);
      setOpenApprove(false);
    } finally {
      setIsApproving(false);
    }
  };

  const handleCustomerInputChange = (event: any, newValue: string) => {
    setInputCustomerValue(newValue);
  };

  useEffect(() => {
    debouncedFetchCustomer(inputCustomerValue);
    // customerService.getAll(subsidiaryId).then((customersData) => {
    //   if (!customersData.length) return;
    //   setClients(customersData.map((x: any) => ({ key: x.id, value: x.displayName })));
    // });
  }, [inputCustomerValue]);

  const getRowsToReport = () => rowsTable.map((x) => ({
    Data: x.createdAt ? format(new Date(x.createdAt), 'dd/MM/yyyy HH:mm') : '',
    Cliente: x.description,
    CPF: x.pixPayerCpf ?? '',
    Pagador: x.pixPayerName ?? '',
    Pagamento: x.payment,
    Status: x.status,
    Valor: x.value,
    'Valor do crédito': x.creditValue
  }));

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={openApprove}
        onClose={handleCloseApprove}
        aria-labelledby="approve-dialog-title"
      >
        <DialogTitle id="approve-dialog-title">
          Confirma a aprovacão do crédito?
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: 30 }} className="text-center">
            <strong className={`${selectedRow?.isPositive ? 'text-success' : 'text-danger'}`}>
              {selectedRow?.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
            </strong>
            <br />
            <small>para</small>
            <br />
            <strong>
              {selectedRow?.description}
            </strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleCloseApprove} disabled={isApproving}>Cancelar</Button>
          <Button onClick={handleConfirm} autoFocus disabled={isApproving}>
            {isApproving ? 'Aprovando...' : 'Aprovar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullScreen={fullScreen}
        open={openDetail}
        onClose={handleCloseDetail}
        aria-labelledby="detail-dialog-title"
        maxWidth={selectedRow?.receiptUrl ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle id="detail-dialog-title">
          Detalhe da solicitação
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={selectedRow.receiptUrl ? 6 : 12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Data da solicitação</small>
                    <strong>
                      {selectedRow.createdAt ? toBrazilDateTime(new Date(selectedRow.createdAt)) : '-'}
                    </strong>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Status</small>
                    <strong>{selectedRow.status || '-'}</strong>
                  </Grid>
                  <Grid item xs={12}>
                    <small className="d-block text-black-50">Cliente</small>
                    <strong>{selectedRow.description}</strong>
                    {(selectedRow.pixPayerCpf || selectedRow.pixPayerName) && (
                      <small className="d-block text-black-50">
                        {[selectedRow.pixPayerCpf, selectedRow.pixPayerName].filter(Boolean).join(' — ')}
                      </small>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Pagamento</small>
                    <strong>{selectedRow.payment || '-'}</strong>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Saldo anterior</small>
                    <strong>
                      {selectedRow.previousBalance.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                    </strong>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Valor da recarga</small>
                    <strong className={selectedRow.isPositive ? 'text-success' : 'text-danger'}>
                      {selectedRow.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                    </strong>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Valor do crédito</small>
                    <strong className={selectedRow.isPositive ? 'text-success' : 'text-danger'}>
                      {selectedRow.creditValue.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                    </strong>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Aprovado por</small>
                    <strong>{selectedRow.approvedBy || '-'}</strong>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <small className="d-block text-black-50">Data da aprovação</small>
                    <strong>
                      {selectedRow.approvedDate ? toBrazilDateTime(new Date(selectedRow.approvedDate)) : '-'}
                    </strong>
                  </Grid>
                  {selectedRow.comments && (
                    <Grid item xs={12}>
                      <small className="d-block text-black-50">Observações</small>
                      <strong>{selectedRow.comments}</strong>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <small className="d-block text-black-50">Comprovante</small>
                    {selectedRow.receiptUrl ? (
                      <a
                        href={selectedRow.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-weight-bold"
                      >
                        Abrir comprovante
                      </a>
                    ) : (
                      <strong>-</strong>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {selectedRow.receiptUrl && (
                <Grid item xs={12} md={6}>
                  <small className="d-block text-black-50 mb-2">Preview do comprovante</small>
                  <Box
                    className="border rounded p-2 bg-light"
                    style={{
                      minHeight: isMobile ? 220 : 320,
                      maxHeight: isMobile ? 280 : 480,
                      overflow: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {getReceiptPreviewKind(selectedRow.receiptUrl) === 'image' && (
                      <img
                        src={selectedRow.receiptUrl}
                        alt="Comprovante"
                        style={{
                          maxWidth: '100%',
                          maxHeight: isMobile ? 240 : 440,
                          objectFit: 'contain'
                        }}
                      />
                    )}
                    {getReceiptPreviewKind(selectedRow.receiptUrl) === 'pdf' && (
                      <iframe
                        title="Preview do comprovante"
                        src={selectedRow.receiptUrl}
                        style={{
                          width: '100%',
                          height: isMobile ? 240 : 440,
                          border: 'none'
                        }}
                      />
                    )}
                    {getReceiptPreviewKind(selectedRow.receiptUrl) === 'other' && (
                      <div className="text-center p-3">
                        <p className="mb-2">Pré-visualização indisponível para este arquivo.</p>
                        <a
                          href={selectedRow.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-weight-bold"
                        >
                          Abrir comprovante
                        </a>
                      </div>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail} autoFocus>Fechar</Button>
        </DialogActions>
      </Dialog>
      <WrapperSimple sectionHeading="Solicitações de créditos no pré-pago">
        <Formik
          initialValues={{
            client: { key: null, value: 'Todos' },
            paymentType: { key: null, value: 'Todos' },
            status: { key: null, value: 'Todos' },
            startDate: getFirstDate(),
            endDate: getLastDate()
          }}
          validationSchema={Yup.object().shape({
            client: Yup.object().required().nullable(),
            paymentType: Yup.object().required().nullable(),
            status: Yup.object().required().nullable(),
            startDate: Yup.date().required().nullable(),
            endDate: Yup.date().required().nullable()
          })}
          onSubmit={async (values: Formulario, actions) => {
            setIsLoading(true);
            values.endDate?.setSeconds(59);
            setValuesForm(values);

            balanceService.getPrePagoReportAsync(subsidiaryId, values.startDate!, values.endDate!, values.client?.key).then(async (report) => {
              let filteredReport = report;

              if (values.paymentType?.key != null) {
                filteredReport = filteredReport.filter(
                  (w) => Number(w.payment) === Number(values.paymentType?.key)
                );
              }

              if (values.status?.key != null) {
                filteredReport = filteredReport.filter(
                  (w) => w.status === values.status?.key
                );
              }

              const approverNames = await resolveApproverNames(filteredReport.map((w) => w.approvedBy));

              setRows(filteredReport.map((w) => {
                let customIcon: IconName = 'question';
                switch (w.status) {
                  case balanceStatus.Approved:
                    customIcon = 'check-circle';
                    break;
                  case balanceStatus.Canceled:
                    customIcon = 'times-circle';
                    break;
                  case balanceStatus.Waiting:
                    customIcon = 'hourglass-half';
                    break;
                  case balanceStatus.Undefined:
                    customIcon = 'question';
                    break;
                  default:
                    break;
                }

                return {
                  approvedBy: w.approvedBy
                    ? (approverNames[w.approvedBy] ?? w.approvedBy)
                    : '',
                  approvedDate: w.approvedDate,
                  comments: w.comments ?? null,
                  createdAt: w.createdAt,
                  id: w.id,
                  description: w.owner.name,
                  payment: getPaymentDescription(w.payment),
                  previousBalance: (w.previousBalance ?? 0) / 100,
                  receiptUrl: getReceiptUrl(w),
                  status: BalanceStatusText[w.status],
                  icon: customIcon,
                  isPositive: w.type === 'addition',
                  enableButton: canApprove
                    && w.status === balanceStatus.Waiting
                    && w.type === 'addition'
                    && (w.pix == null),
                  value: w.value,
                  creditValue: Number(w.payment) === PaymentBalanceType.PixOnline
                    ? (w.pixRepasseValue ?? w.value)
                    : w.value,
                  pixPayerCpf: Number(w.payment) === PaymentBalanceType.PixOnline
                    ? (w.pix?.gnExtras?.pagador?.cpf ?? null)
                    : null,
                  pixPayerName: Number(w.payment) === PaymentBalanceType.PixOnline
                    ? (w.pix?.gnExtras?.pagador?.nome ?? null)
                    : null
                };
              }));
            }).catch((e) => {
              enqueueSnackbar(e.message, { variant: 'error' });
            }).finally(() => {
              actions.setSubmitting(false);
              setIsLoading(false);
            });
          }}
        >
          {(props: FormikProps<Formulario>) => {
            const {
              values,
              errors,
              touched,
              handleBlur,
              isSubmitting,
              setFieldValue
            } = props;

            return (
              <Form noValidate autoComplete="off">
                <Field type="hidden" name="address.ibge" value="" />
                <Grid container spacing={isMobile ? 2 : 3}>
                  <Grid item xs={12} sm={6} md={2}>
                    <AutocompleteCustomField
                      name="client"
                      options={clients}
                      onInputChange={handleCustomerInputChange}
                      noOptionsText="Informe um cliente"
                      onBlur={handleBlur}
                      errors={errors.client}
                      touched={touched.client}
                      label="Clientes"
                      value={values.client}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <AutocompleteCustomField
                      name="paymentType"
                      options={paymentTypeOptions}
                      onInputChange={() => {}}
                      noOptionsText="Nenhum tipo encontrado"
                      onBlur={handleBlur}
                      errors={errors.paymentType}
                      touched={touched.paymentType}
                      label="Pagamento"
                      value={values.paymentType}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <AutocompleteCustomField
                      name="status"
                      options={statusOptions}
                      onInputChange={() => {}}
                      noOptionsText="Nenhum status encontrado"
                      onBlur={handleBlur}
                      errors={errors.status}
                      touched={touched.status}
                      label="Status"
                      value={values.status}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KeyboardDateTimePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Início"
                      format={isMobile ? 'dd/MM/yy HH:mm' : 'dd/MM/yyyy HH:mm'}
                      ampm={false}
                      InputLabelProps={{ shrink: true }}
                      name="startDate"
                      onChange={(date: Date | null) => { setFieldValue('startDate', date); }}
                      onBlur={handleBlur}
                      value={values.startDate}
                      error={Boolean(errors.startDate && touched.startDate)}
                      helperText={errors.startDate && touched.startDate && errors.startDate}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <KeyboardDateTimePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Fim"
                      format={isMobile ? 'dd/MM/yy HH:mm' : 'dd/MM/yyyy HH:mm'}
                      ampm={false}
                      InputLabelProps={{ shrink: true }}
                      name="endDate"
                      onChange={(date: Date | null) => { setFieldValue('endDate', date); }}
                      onBlur={handleBlur}
                      value={values.endDate}
                      error={Boolean(errors.endDate && touched.endDate)}
                      helperText={errors.endDate && touched.endDate && errors.endDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} fullWidth>
                      <span className="btn-wrapper--icon">
                        <FontAwesomeIcon icon={['far', 'keyboard']} />
                      </span>
                      <span className="btn-wrapper--label">Buscar</span>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </WrapperSimple>
      {isLoading ? <Loading /> : (
        <>
          {
            rowsTable && (
              rowsTable.length > 0 ? (
                <Card className="card-box mb-4">
                  <div className="card-header">
                    <div className="card-header--title">
                      {isMobile && (
                        <small className="text-black-50">
                          {rowsTable.length}
                          {' '}
                          solicitação(ões)
                        </small>
                      )}
                    </div>
                    <Box className="card-header--actions">
                      {' | '}
                      <ExportPDF
                        document={(
                          <PrepagPrint
                            data={rowsTable}
                            startDate={valuesForm!.startDate}
                            endDate={valuesForm!.endDate}
                          />
                        )}
                        fileName={format(new Date(), 'yyyyMMdd_hhmmss')}
                      />
                      {' | '}
                      <ExportCSV csvData={getRowsToReport()} fileName={format(new Date(), 'yyyyMMdd_hhmmss')} />
                    </Box>
                  </div>
                  <CardContent className="p-0">
                    {isMobile ? (
                      <Box className="p-2">
                        {rowsTable.map((x) => (
                          <Box
                            key={x.id}
                            className="border rounded mb-2 p-3"
                            onClick={() => handleClickOpen(x)}
                            style={{
                              cursor: (!canApprove || x.enableButton) ? 'pointer' : 'default',
                              backgroundColor: '#fff'
                            }}
                          >
                            <Box className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <div className="font-weight-bold text-black">{x.description}</div>
                                {(x.pixPayerCpf || x.pixPayerName) && (
                                  <small className="d-block text-black-50">
                                    {[x.pixPayerCpf, x.pixPayerName].filter(Boolean).join(' — ')}
                                  </small>
                                )}
                              </div>
                              <span className="d-inline-flex align-items-center">
                                <FontAwesomeIcon icon={['fas', x.icon]} title={x.status} className="mr-1" />
                                <small>{x.status}</small>
                              </span>
                            </Box>
                            <Box className="d-flex justify-content-between text-black-50 mb-1">
                              <small>
                                {x.createdAt
                                  ? `${toBrazilDate(new Date(x.createdAt))} ${toBrazilTime(new Date(x.createdAt))}`
                                  : '-'}
                              </small>
                              <small>{x.payment}</small>
                            </Box>
                            <Box className="d-flex justify-content-between align-items-end mt-2">
                              <div>
                                <small className="d-block text-black-50">Recarga</small>
                                <strong className={x.isPositive ? 'text-success' : 'text-danger'}>
                                  {x.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                                </strong>
                              </div>
                              <div className="text-right">
                                <small className="d-block text-black-50">Crédito</small>
                                <strong className={x.isPositive ? 'text-success' : 'text-danger'}>
                                  {x.creditValue.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                                </strong>
                              </div>
                            </Box>
                            {!canApprove && (
                              <small className="d-block text-primary mt-2">Toque para ver detalhes</small>
                            )}
                            {canApprove && x.enableButton && (
                              <small className="d-block text-primary mt-2">Toque para aprovar</small>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <div className="table-responsive">
                        <table className="text-nowrap mb-0 table table-borderless table-hover">
                          <thead>
                            <tr>
                              <th className="text-center" title="Data">Data</th>
                              <th className="text-left" title="Nome do cliente">Cliente</th>
                              <th className="text-left" title="Tipo de pagamento">Pagamento</th>
                              <th className="text-center" title="Status">Status</th>
                              <th className="text-right" title="Valor da recarga">Valor da recarga</th>
                              <th className="text-right" title="Valor do crédito">Valor do crédito</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowsTable.map((x) => (
                              <tr
                                key={x.id}
                                className={!canApprove || x.enableButton ? classes.selectRow : undefined}
                                onClick={() => handleClickOpen(x)}
                              >
                                <td className="text-center">
                                  {x.createdAt && (
                                    <div>
                                      <div>{toBrazilDate(new Date(x.createdAt))}</div>
                                      <span>{toBrazilTime(new Date(x.createdAt))}</span>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="font-weight-bold text-black">
                                      {x.description}
                                      {(x.pixPayerCpf || x.pixPayerName) && (
                                        <small className="d-block font-weight-normal text-black-50">
                                          {[x.pixPayerCpf, x.pixPayerName].filter(Boolean).join(' — ')}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-left">
                                  {x.payment}
                                </td>
                                {/* eslint-disable-next-line */}
                                <td className="text-center">
                                  <FontAwesomeIcon icon={['fas', x.icon]} title={x.status} />
                                </td>
                                <td className={`text-right ${x.isPositive ? 'text-success' : 'text-danger'}`}>
                                  {x.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                                </td>
                                <td className={`text-right ${x.isPositive ? 'text-success' : 'text-danger'}`}>
                                  {x.creditValue.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : <NoData message="Nenhuma informação encontrada." />
            )
          }
        </>
      )}
    </>
  );
};

export default page;
